import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

const sectionStatus = z.object({
  student: z.enum(["empty", "started", "done"]),
  schooling: z.enum(["empty", "started", "done"]),
  guardians: z.enum(["empty", "started", "done"]),
  consent: z.enum(["empty", "started", "done"]),
});

const draftSchema = z.object({
  draftId: z.string().uuid(),
  payload: z.string().max(200_000),
  sectionStatus,
  studentName: z.string().max(160).optional().default(""),
  guardianName: z.string().max(160).optional().default(""),
  guardianEmail: z.string().max(255).optional().default(""),
  guardianPhone: z.string().max(60).optional().default(""),
  studentsCount: z.number().int().min(1).max(6),
  locale: z.enum(["sv", "en", "es"]).optional().default("sv"),
});

export const saveEnrollmentDraft = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => draftSchema.parse(data))
  .handler(async ({ data }) => {
    let payload: unknown;
    try {
      payload = JSON.parse(data.payload);
    } catch {
      return { ok: false as const };
    }

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    // Samma familj ska inte ge en ny rad varje gång formuläret laddas om.
    // Finns ett färskt, ej inskickat utkast från samma vårdnadshavare (eller
    // samma elevnamn) återanvänds det id:t i stället för att skapa ett nytt.
    let targetId = data.draftId;
    const since = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
    const match =
      (data.guardianEmail && { column: "guardian_email", value: data.guardianEmail }) ||
      (data.guardianPhone && { column: "guardian_phone", value: data.guardianPhone }) ||
      (data.studentName && { column: "student_name", value: data.studentName }) ||
      null;
    if (match) {
      const { data: existing } = await supabaseAdmin
        .from("enrollment_drafts")
        .select("draft_id")
        .is("submitted_at", null)
        .eq(match.column, match.value)
        .gte("updated_at", since)
        .order("updated_at", { ascending: false })
        .limit(1);
      const found = existing?.[0]?.draft_id;
      if (found) targetId = found;
    }

    const { error } = await supabaseAdmin.from("enrollment_drafts").upsert(
      {
        draft_id: targetId,
        payload: payload as never,
        section_status: data.sectionStatus as never,
        student_name: data.studentName || null,
        guardian_name: data.guardianName || null,
        guardian_email: data.guardianEmail || null,
        guardian_phone: data.guardianPhone || null,
        students_count: data.studentsCount,
        locale: data.locale ?? "sv",
        updated_at: new Date().toISOString(),
        last_seen_at: new Date().toISOString(),
      },
      { onConflict: "draft_id" },
    );

    if (error) {
      console.error("draft upsert failed", error);
      return { ok: false as const };
    }

    return { ok: true as const };
  });

/**
 * Livssignal: familjen har formuläret öppet. Sätter bara tidpunkten på ett
 * redan sparat utkast – ingen persondata skickas in eller ut.
 */
export const touchEnrollmentDraft = createServerFn({ method: "POST" })
  .inputValidator((data: unknown) => z.object({ draftId: z.string().uuid() }).parse(data))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    await supabaseAdmin
      .from("enrollment_drafts")
      .update({ last_seen_at: new Date().toISOString() })
      .eq("draft_id", data.draftId)
      .is("submitted_at", null);
    return { ok: true as const };
  });
